import { fromJS } from 'immutable'
import { createReducer } from 'utils'
import { ADMIN } from 'constants/UserRoles'

import { AUTH_USER_REQUEST, LOGIN_REQUEST, LOGOUT } from 'common/actions/auth'

import {
  LOAD_SYSTEMS_REQUEST,
  UPDATE_RESET_SYSTEM_REQUEST,
  UPDATE_DEVICE_NAME_REQUEST,
  LOAD_SYSTEM_REQUEST,
  LOAD_LAST_VERSIONS_REQUEST,
  POLL_SYSTEMS_REQUEST,
  DELETE_SYSTEMS_REQUEST,
  CREATE_API_KEY_REQUEST,
  DELETE_API_KEY_REQUEST,
  SET_API_KEY_STATUS_REQUEST
} from 'common/actions/system'

import {
  LOAD_USERS_REQUEST,
  UPDATE_USER_API_SERVICE_REQUEST,
  UPDATE_USER_LIMIT_REQUEST,
  UPDATE_USER_API_SERVICE_LIMIT_REQUEST,
  UPDATE_USER_PREMIUM_REQUEST,
  ADD_SYSTEM_TO_USER_REQUEST
} from 'common/actions/user'

export const initialState = fromJS({
  users: {},
  systems: {},
  lastVersions: {},
  models: {},
  device: {}
})

const mergeEntities = (state, { payload }) => {
  return state.withMutations(state =>
    Object.keys(payload.entities).reduce(
      (_state, entity) => _state.mergeDeepIn([entity], payload.entities[entity]),
      state
    )
  )
}

const replaceUsers = (state, { payload }) => {
  const usersMap = payload.entities?.users ? fromJS(payload.entities.users) : fromJS({})
  const adminUsers = state.get('users', fromJS({})).filter(user => user.get('role') === ADMIN)

  return state.setIn(['users'], usersMap.merge(adminUsers))
}

const replaceEntities = (state, { payload }) => {
  let immutableMap
  if (payload.entities.systems) {
    immutableMap = fromJS(payload.entities.systems)
  } else {
    immutableMap = fromJS([])
  }

  return state.setIn(['systems'], immutableMap)
}

const getCreatedApiKeyDevices = payload => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.devices)) return payload.devices
  if (Array.isArray(payload?.data)) return payload.data
  if (payload?.device) return [payload.device]
  if (payload?.data?.deviceId || payload?.data?._id || payload?.data?.systemId) {
    return [payload.data]
  }
  if (payload?.deviceId || payload?._id || payload?.systemId) return [payload]

  return []
}

export default createReducer(initialState, {
  // User actions
  [LOGOUT]: () => initialState,
  [AUTH_USER_REQUEST.SUCCESS]: mergeEntities,
  [LOGIN_REQUEST.SUCCESS]: mergeEntities,

  // System actions
  [LOAD_SYSTEMS_REQUEST.SUCCESS]: replaceEntities,
  [LOAD_LAST_VERSIONS_REQUEST.SUCCESS]: mergeEntities,
  [POLL_SYSTEMS_REQUEST.SUCCESS]: mergeEntities,
  [LOAD_SYSTEM_REQUEST.SUCCESS]: mergeEntities,
  [UPDATE_RESET_SYSTEM_REQUEST.SUCCESS]: (state, { payload: { data } }) => {
    return state.updateIn(['systems'], systems =>
      systems.map(s => {
        if (s.get('_id') !== data.deviceId) return s
        return s.set('reset', data.data.reset)
      })
    )
  },
  [UPDATE_DEVICE_NAME_REQUEST.SUCCESS]: (state, { payload: { data } }) => {
    const device = data.device || {}
    const deviceId = data.deviceId || device.DeviceId
    const nextName = device.Meta && device.Meta.name ? device.Meta.name : data.newName

    return state.updateIn(['systems'], systems =>
      systems.map(s => {
        if (s.get('_id') !== deviceId) return s
        return s.set('name', nextName)
      })
    )
  },
  [DELETE_SYSTEMS_REQUEST.SUCCESS]: (state, { payload: { data } }) => {
    return state.updateIn(['systems'], systems =>
      systems.filter(s => !data.ids.includes(s.get('_id')))
    )
  },
  [CREATE_API_KEY_REQUEST.SUCCESS]: (state, { payload }) => {
    const devices = getCreatedApiKeyDevices(payload)

    return state.updateIn(['systems'], systems =>
      systems.map(system => {
        const updatedDevice = devices.find(
          device =>
            device.deviceId === system.get('_id') ||
            device._id === system.get('_id') ||
            device.systemId === system.get('_id')
        )

        if (!updatedDevice) return system

        return system.withMutations(nextSystem => {
          nextSystem.set('apiKey', updatedDevice.apiKey)

          if (updatedDevice.apiKeyDate) {
            nextSystem.set('apiKeyDate', updatedDevice.apiKeyDate)
          }

          nextSystem.set('apiKeyDisabled', Boolean(updatedDevice.apiKeyDisabled))
        })
      })
    )
  },
  [DELETE_API_KEY_REQUEST.SUCCESS]: (state, { payload: { deviceId } }) => {
    return state.updateIn(['systems'], systems =>
      systems.map(system => {
        if (system.get('_id') !== deviceId) return system

        return system.withMutations(nextSystem => {
          nextSystem.set('apiKey', undefined)
          nextSystem.set('apiKeyDate', undefined)
          nextSystem.set('apiKeyDisabled', undefined)
        })
      })
    )
  },
  [SET_API_KEY_STATUS_REQUEST.SUCCESS]: (state, { payload: { deviceId, apiKeyDisabled } }) => {
    return state.updateIn(['systems'], systems =>
      systems.map(system => {
        if (system.get('_id') !== deviceId) return system

        return system.set('apiKeyDisabled', apiKeyDisabled)
      })
    )
  },
  [ADD_SYSTEM_TO_USER_REQUEST.SUCCESS]: (state, { payload }) => {
    const systems = payload?.entities?.systems

    if (!systems) {
      return state
    }

    return state.mergeDeepIn(['systems'], fromJS(systems))
  },

  // User actions
  [LOAD_USERS_REQUEST.SUCCESS]: replaceUsers,
  [UPDATE_USER_PREMIUM_REQUEST.SUCCESS]: (state, { payload: { data } }) => {
    return state.updateIn(['users'], users =>
      users.map(u => {
        if (u.get('_id') !== data.customerId) return u
        return u.set('isPremium', data.data.isPremium)
      })
    )
  },
  [UPDATE_USER_API_SERVICE_REQUEST.SUCCESS]: (state, { payload: { data } }) => {
    return state.updateIn(['users'], users =>
      users.map(u => {
        if (u.get('_id') !== data.customerId) return u
        return u.set('apiService', data.data.apiService)
      })
    )
  },
  [UPDATE_USER_LIMIT_REQUEST.SUCCESS]: (state, { payload: { data } }) => {
    return state.updateIn(['users'], users =>
      users.map(u => {
        if (u.get('_id') !== data.customerId) return u
        return u.set('nDispositivosLimit', data.data.nLimit)
      })
    )
  },
  [UPDATE_USER_API_SERVICE_LIMIT_REQUEST.SUCCESS]: (state, { payload: { data } }) => {
    return state.updateIn(['users'], users =>
      users.map(u => {
        if (u.get('_id') !== data.customerId) return u
        return u.set('apiServiceLimit', data.data.apiServiceLimit)
      })
    )
  }
})
